import { useState } from "react";
import { MessageCircle, Headset, Search, Database, BrainCircuit, Sparkles, Wrench, Cpu, FileSearch, CheckCircle2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import { useLanguage } from "@/context/LanguageContext";

const stageIcons = [MessageCircle, Headset, Search, Database, BrainCircuit, Sparkles];

const stageTools = [
  ["Web", "Chat"],
  ["Chat", "Email", "Helpdesk"],
  ["Search", "Docs", "Web"],
  ["SQL", "BI", "APIs"],
  ["LLM", "Policies"],
  ["Chat", "Email", "CRM"],
];

export default function AgentNetwork() {
  const { t } = useLanguage();
  const stages = t("home.netStages");
  const fields = t("home.netFields");
  const [active, setActive] = useState(1);
  const stage = stages[active];
  const Icon = stageIcons[active];

  return (
    <section className="py-24 border-t border-white/8 bg-[#07111F]/40 relative overflow-hidden" data-testid="agent-network-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="text-center">
          <SectionLabel>{t("home.netLabel")}</SectionLabel>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-3xl mx-auto">
            {t("home.netH2")}
          </h2>
          <p className="mt-3 text-sm text-slate-400">{t("home.netSub")}</p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-14 flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
            {stages.map((s, i) => {
              const NodeIcon = stageIcons[i];
              const isActive = i === active;
              return (
                <div key={s.name} className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 lg:flex-1">
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    onClick={() => setActive(i)}
                    className={`relative flex items-center lg:flex-col gap-3 rounded-2xl px-4 py-4 lg:py-6 text-left lg:text-center transition-all duration-300 w-full ${
                      isActive
                        ? "bg-gradient-to-b from-[#4F8CFF]/20 to-[#7C5CFF]/10 border border-[#4F8CFF]/50 shadow-[0_0_30px_-8px_rgba(79,140,255,0.6)]"
                        : "vora-card hover:border-[#4F8CFF]/30"
                    }`}
                    data-testid={`network-stage-${i}`}
                  >
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isActive ? "bg-[#4F8CFF] text-white" : "bg-[#4F8CFF]/12 text-[#4F8CFF]"}`}>
                      <NodeIcon className="h-5 w-5" />
                    </span>
                    <span className={`text-sm font-medium ${isActive ? "text-white" : "text-slate-300"}`}>{s.name}</span>
                    <span className={`absolute top-2.5 right-2.5 h-1.5 w-1.5 rounded-full ${isActive ? "bg-emerald-400 vora-pulse-dot" : "bg-[#4F8CFF]/50"}`} aria-hidden />
                  </button>
                  {i < stages.length - 1 && (
                    <div className="net-connector lg:flex-none lg:w-8 h-6 lg:h-px w-px lg:w-8 self-center bg-gradient-to-r lg:from-[#4F8CFF]/50 lg:to-[#7C5CFF]/50 from-[#4F8CFF]/50 to-[#7C5CFF]/50" aria-hidden>
                      <span className="net-flow-dot hidden lg:block" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-10 vora-glass rounded-3xl p-6 sm:p-8" data-testid="network-detail-panel">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#4F8CFF] to-[#7C5CFF] text-white">
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">{t("home.netLabel")}</p>
                <p className="text-lg font-semibold text-white">{stage.name}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4">
                <div className="flex items-center gap-2 text-[#8fb4ff] text-xs font-mono uppercase tracking-wider"><Cpu className="h-3.5 w-3.5" />{fields.does}</div>
                <p className="mt-2 text-sm text-slate-200" data-testid="network-detail-does">{stage.does}</p>
              </div>
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4">
                <div className="flex items-center gap-2 text-[#8fb4ff] text-xs font-mono uppercase tracking-wider"><Wrench className="h-3.5 w-3.5" />{fields.tools}</div>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="network-detail-tools">
                  {stageTools[active].map((tool) => (
                    <span key={tool} className="text-xs rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/8 px-2.5 py-1 text-slate-200">{tool}</span>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4">
                <div className="flex items-center gap-2 text-[#8fb4ff] text-xs font-mono uppercase tracking-wider"><FileSearch className="h-3.5 w-3.5" />{fields.processes}</div>
                <p className="mt-2 text-sm text-slate-200" data-testid="network-detail-processes">{stage.processes}</p>
              </div>
              <div className="rounded-2xl border border-[#4F8CFF]/20 bg-[#4F8CFF]/5 p-4">
                <div className="flex items-center gap-2 text-[#8fb4ff] text-xs font-mono uppercase tracking-wider"><CheckCircle2 className="h-3.5 w-3.5" />{fields.result}</div>
                <p className="mt-2 text-sm text-white font-medium" data-testid="network-detail-result">{stage.result}</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
